import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Loader2, ArrowLeft, CheckCircle, AlertCircle, Linkedin, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

// Terms and Conditions Content Component
const TermsContent = () => (
  <div className="prose prose-sm max-w-none text-slate-700">
    <p className="text-sm text-slate-500 mb-4"><strong>Effective Date:</strong> January 15, 2026</p>
    <p className="text-sm text-slate-500 mb-6"><strong>Company:</strong> ARSENAL Advisors LLC (Massachusetts, USA)</p>
    <p className="text-sm text-slate-500 mb-6"><strong>Product:</strong> Varyynt – the AI-driven job matching platform</p>
    
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <p className="text-amber-800 font-medium text-sm">
        PLEASE READ THESE TERMS CAREFULLY. By accessing or using Varyynt (the "Service"), you agree to be bound by this Terms of Use Agreement ("Agreement"). If you do not agree, you may not use the Service.
      </p>
    </div>
    
    <p className="mb-6">This Agreement is a legally binding contract between you ("User") and <strong>ARSENAL Advisors LLC</strong> ("Company," "we," "our," "us").</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">1. Description of the Service</h2>
    <p>Varyynt is an AI-powered career matching platform for life sciences professionals. The Service:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Collects User-provided information, including resume data, preferences, and career interests</li>
      <li>Uses algorithms and proprietary scoring (V-FIT Index) to evaluate job listings</li>
      <li>Displays job opportunities based on relevance and predicted fit</li>
      <li>Provides career-related analytics, insights, and notifications</li>
    </ul>
    <p className="font-medium">The Service does not guarantee outcomes, employment, job availability, or accuracy of job listings.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">2. Eligibility</h2>
    <p>Users must:</p>
    <ol className="list-decimal pl-6 space-y-1 mb-4">
      <li>Be at least 18 years old</li>
      <li>Provide accurate information</li>
      <li>Maintain an active and verifiable email address</li>
      <li>Provide a valid LinkedIn profile URL</li>
      <li>Agree not to misuse or attempt to reverse engineer the Service</li>
    </ol>
    <p>We may refuse or revoke access at our sole discretion.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">3. No Employment Guarantee</h2>
    <p className="font-medium mb-2">The Company does NOT:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Guarantee job placement</li>
      <li>Guarantee interviews</li>
      <li>Guarantee hiring outcomes</li>
      <li>Guarantee accuracy of job matches</li>
      <li>Guarantee that any employer will contact, review, or consider you</li>
      <li>Validate or verify employer-supplied job details</li>
      <li>Provide professional, legal, financial, or hiring advice</li>
    </ul>
    <div className="bg-slate-100 rounded-lg p-4 mb-4">
      <p className="font-semibold text-slate-900">THE SERVICE IS PROVIDED "AS IS," WITHOUT WARRANTY.</p>
      <p className="mt-2">Varyynt is a tool, not an employment agency. Varyynt does not act as a recruiter or a representative for any employer.</p>
    </div>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">4. Job Listings and Third-Party Data Disclaimer</h2>
    <p>Job listings may originate from:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Employers</li>
      <li>Third-party data feeds</li>
      <li>Publicly available sources</li>
      <li>Automated ingestion and parsing systems</li>
    </ul>
    <p className="mb-2">We do <strong>not</strong> control or guarantee:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Accuracy, Completeness, Availability, Timeliness</li>
      <li>Legitimacy, Compensation details, Company descriptions</li>
      <li>Hiring practices</li>
    </ul>
    <p className="font-medium">Users assume full responsibility for verifying all job information independently.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">5. User Responsibilities</h2>
    <p>You agree to:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Provide accurate information</li>
      <li>Refrain from fraudulent activities</li>
      <li>Not scrape, copy, or distribute platform content</li>
      <li>Not use the Service to harass, discriminate, or violate laws</li>
      <li>Keep your login credentials secure</li>
      <li>Use Varyynt only for personal career purposes</li>
    </ul>
    <p>You are solely responsible for the accuracy of your resume, profile, and preferences.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">6. V-FIT Score Disclaimer</h2>
    <p>The V-FIT Index is a <strong>heuristic prediction tool</strong>, not a formal assessment.</p>
    <p className="mb-2">You acknowledge that:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>V-FIT scores may be inaccurate or incomplete</li>
      <li>V-FIT results are opinion-based algorithmic assessments</li>
      <li>V-FIT does not provide guarantees or assurances</li>
      <li>Scores may change as data updates or algorithms evolve</li>
      <li>You are solely responsible for validating job fit independently</li>
    </ul>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">7. No Professional Advice</h2>
    <p>The Service does not provide:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Career counseling</li>
      <li>Legal advice</li>
      <li>Financial advice</li>
      <li>Professional hiring advice</li>
      <li>Compensation guidance</li>
    </ul>
    <p>All insights are algorithmic predictions and <strong>not</strong> definitive guidance.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">8. Privacy & Data Handling</h2>
    <p>Usage of the Service is also governed by our Privacy Policy. Varyynt collects and processes:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Personal data, Resume contents, Employment history</li>
      <li>Preferences, Activity data, Metadata for analytics</li>
    </ul>
    <p className="mb-2">We may use this data to:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Improve matching and enhance algorithms</li>
      <li>Train systems (non-identifiable data only)</li>
      <li>Operate and maintain the platform</li>
    </ul>
    <p className="font-medium">We will never sell your personal data.</p>
    <p>We may anonymize aggregated data for research, analytics, and product development.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">9. Intellectual Property</h2>
    <p>All content, algorithms, designs, V-FIT scoring logic, and branding—including <strong>Varyynt</strong> and all related marks—are exclusive property of ARSENAL Advisors LLC.</p>
    <p className="mb-2">Users are prohibited from:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Reverse engineering, Copying, Reproducing</li>
      <li>Reselling, Redistributing, Creating derivative works</li>
      <li>Using the Service for competitive analysis</li>
    </ul>
    <p>Without our express written consent.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">10. Limitation of Liability</h2>
    <p>To the maximum extent permitted by law:</p>
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <p className="font-semibold text-red-900 mb-2">ARSENAL ADVISORS LLC SHALL NOT BE LIABLE FOR ANY:</p>
      <ul className="list-disc pl-6 space-y-1 text-red-800 text-sm">
        <li>Lost profits or opportunities</li>
        <li>Job placement failures</li>
        <li>Incorrect or outdated job listings</li>
        <li>Algorithmic errors or hiring outcomes</li>
        <li>Damages resulting from reliance on job matches</li>
        <li>Data loss, unauthorized access, service interruptions</li>
        <li>Third-party actions or omissions</li>
      </ul>
    </div>
    <p className="font-medium">Total liability for any claim shall not exceed $50 or the amount you paid (if any), whichever is less.</p>
    <p>If you disagree with this limitation, you must stop using the Service immediately.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">11. No Warranties</h2>
    <p>The Service is provided <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong>, with no warranties, express or implied, including but not limited to:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Accuracy, Reliability, Availability, Performance</li>
      <li>Fitness for a job search purpose</li>
      <li>Merchantability, Non-infringement</li>
    </ul>
    <p className="font-medium">Use the Service at your own risk.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">12. User Termination</h2>
    <p>We may suspend or terminate accounts for:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Incomplete or invalid onboarding</li>
      <li>False information</li>
      <li>Abuse or misuse</li>
      <li>Violations of these Terms</li>
      <li>Attempted exploitation or reverse engineering</li>
    </ul>
    <p>Upon termination, access to the Service is immediately revoked.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">13. Modifications</h2>
    <p>ARSENAL Advisors LLC may update or modify this Agreement at any time. Continued use after changes constitutes acceptance.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">14. Governing Law</h2>
    <p>This Agreement is governed by the laws of:</p>
    <p className="font-semibold">The Commonwealth of Massachusetts, USA.</p>
    <p>All disputes must be resolved exclusively in courts located in Massachusetts.</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">15. Indemnification</h2>
    <p>You agree to indemnify and hold harmless ARSENAL Advisors LLC, its officers, employees, affiliates, and partners from any claims arising from:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>Your use of the Service</li>
      <li>Your reliance on job matches</li>
      <li>Your actions in the hiring process</li>
      <li>Your interactions with employers</li>
      <li>Your violations of any laws or regulations</li>
      <li>Your breach of this Agreement</li>
    </ul>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">16. Contact</h2>
    <p><strong>ARSENAL Advisors LLC</strong></p>
    <p>Massachusetts, USA</p>
    <p>Email: support@varyynt.com</p>

    <h2 className="text-lg font-semibold text-slate-900 mt-8 mb-4">17. Acceptance</h2>
    <p>By creating an account, accessing the dashboard, or submitting your email or resume, you acknowledge:</p>
    <ul className="list-disc pl-6 space-y-1 mb-4">
      <li>You have read and understood this Agreement</li>
      <li>You agree to be bound by it</li>
      <li>You accept all disclaimers and limitations</li>
    </ul>
    <div className="bg-evergreen-950 text-white rounded-lg p-4 mt-6">
      <p className="font-semibold">If you do not accept, you may not use Varyynt.</p>
    </div>
  </div>
);

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref') || searchParams.get('referral');
    if (ref) {
      setReferralCode(ref);
    }
  }, [searchParams]);

  // Normalize LinkedIn URL - auto-fix common mistakes
  const normalizeLinkedInUrl = (url) => {
    if (!url) return url;
    
    let normalized = url.trim();
    
    // Remove any trailing slashes for consistency
    normalized = normalized.replace(/\/+$/, '');
    
    // Handle various input formats users might enter
    // Case: "linkedin.com/in/name" or "www.linkedin.com/in/name"
    if (!normalized.match(/^https?:\/\//i)) {
      // Check if it starts with linkedin.com or www.linkedin.com
      if (normalized.match(/^(www\.)?linkedin\.com/i)) {
        normalized = 'https://' + normalized;
      }
      // Case: "in/name" - user just entered the path
      else if (normalized.match(/^in\/[\w\-]+/i)) {
        normalized = 'https://linkedin.com/' + normalized;
      }
      // Case: "/in/name" - user entered with leading slash
      else if (normalized.match(/^\/in\/[\w\-]+/i)) {
        normalized = 'https://linkedin.com' + normalized;
      }
    }
    
    // Normalize http to https
    normalized = normalized.replace(/^http:\/\//i, 'https://');
    
    // Ensure www. is handled (remove it for consistency)
    normalized = normalized.replace(/^https:\/\/www\./i, 'https://');
    
    return normalized;
  };

  // Extract username from various LinkedIn URL formats for display
  const extractLinkedInUsername = (url) => {
    if (!url) return null;
    const match = url.match(/linkedin\.com\/in\/([\w\-]+)/i);
    return match ? match[1] : null;
  };

  const validateLinkedInUrl = (url) => {
    const normalized = normalizeLinkedInUrl(url);
    const pattern = /^https:\/\/linkedin\.com\/in\/[\w\-]+\/?$/i;
    return pattern.test(normalized);
  };

  // Real-time LinkedIn URL status
  const [linkedinStatus, setLinkedinStatus] = useState({ valid: false, username: null });
  
  useEffect(() => {
    if (linkedinUrl.length > 5) {
      const normalized = normalizeLinkedInUrl(linkedinUrl);
      const isValid = validateLinkedInUrl(linkedinUrl);
      const username = extractLinkedInUsername(normalized);
      setLinkedinStatus({ valid: isValid, username });
    } else {
      setLinkedinStatus({ valid: false, username: null });
    }
  }, [linkedinUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation - Terms MUST be accepted (no exceptions)
    if (!termsAccepted) {
      setError('You must accept the Terms and Conditions to create an account');
      toast.error('Please accept the Terms and Conditions');
      return;
    }

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Normalize the LinkedIn URL before validation
    const normalizedLinkedinUrl = normalizeLinkedInUrl(linkedinUrl);
    
    if (!validateLinkedInUrl(linkedinUrl)) {
      setError('Please enter a valid LinkedIn profile URL (e.g., linkedin.com/in/yourname)');
      return;
    }

    setLoading(true);
    try {
      // Use the normalized URL for registration
      await register(email, password, normalizedLinkedinUrl, referralCode);
      setSuccess(true);
      toast.success('Registration successful! Please check your email.');
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg border-slate-100 animate-slideUp">
          <CardHeader className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-lime-600" />
            </div>
            <CardTitle className="text-2xl font-heading">Check your email</CardTitle>
            <CardDescription className="text-base">
              We've sent a verification link to <span className="font-medium text-slate-900">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 text-center">
              Click the link in your email to verify your account and get started with your career profile.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
              <Link to="/" className="text-center text-sm text-slate-500 hover:text-slate-700">
                Back to home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <Card className="shadow-lg border-slate-100 animate-slideUp">
          <CardHeader className="text-center">
            <Link to="/" className="inline-block mb-4">
              <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
            </Link>
            <CardTitle className="text-2xl font-heading">Create your account</CardTitle>
            <CardDescription>
              Start your personalized career journey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-slideDown">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn Profile URL</Label>
                <div className="relative">
                  <Linkedin className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors ${linkedinStatus.valid ? 'text-green-500' : 'text-slate-400'}`} />
                  <Input
                    id="linkedin"
                    type="text"
                    placeholder="linkedin.com/in/yourname"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    required
                    className={`h-11 pl-10 pr-10 ${linkedinStatus.valid ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : ''}`}
                    data-testid="register-linkedin-input"
                  />
                  {linkedinStatus.valid && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                </div>
                {linkedinStatus.valid && linkedinStatus.username && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Found profile: <span className="font-medium">{linkedinStatus.username}</span>
                  </p>
                )}
                {!linkedinStatus.valid && linkedinUrl.length > 0 && (
                  <p className="text-xs text-amber-600">
                    Tip: Enter your LinkedIn URL like "linkedin.com/in/yourname"
                  </p>
                )}
                {linkedinUrl.length === 0 && (
                  <p className="text-xs text-slate-500">
                    We use your LinkedIn to enrich your profile. Your data stays private.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="h-11"
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-confirm-password-input"
                />
              </div>

              {/* Terms and Conditions Checkbox - MANDATORY */}
              <div className="space-y-2 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked)}
                    className="mt-0.5 data-[state=checked]:bg-evergreen-950 data-[state=checked]:border-evergreen-950"
                    data-testid="terms-checkbox"
                  />
                  <Label 
                    htmlFor="terms" 
                    className="text-sm text-slate-600 font-normal leading-relaxed cursor-pointer"
                  >
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-lime-600 hover:text-lime-700 font-medium underline underline-offset-2 inline-flex items-center gap-1"
                      data-testid="open-terms-modal-btn"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Terms and Conditions
                    </button>
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                </div>
                {!termsAccepted && (
                  <p className="text-xs text-slate-500 pl-7">
                    You must accept the Terms and Conditions to create an account.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-evergreen-950 hover:bg-evergreen-900 disabled:opacity-50"
                disabled={loading || !termsAccepted}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>

              <p className="text-center text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-lime-600 hover:text-teal-700 font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Terms and Conditions Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-evergreen-950 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-lime-400" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-heading">Terms of Use Agreement</DialogTitle>
                  <DialogDescription>
                    ARSENAL Advisors LLC • Varyynt Platform
                  </DialogDescription>
                </div>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            <TermsContent />
          </div>
          
          <div className="flex-shrink-0 pt-4 border-t border-slate-200 mt-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-slate-500">
                By clicking "I Accept", you agree to be bound by these terms.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowTermsModal(false)}
                  data-testid="terms-close-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setTermsAccepted(true);
                    setShowTermsModal(false);
                    toast.success('Terms and Conditions accepted');
                  }}
                  className="bg-evergreen-950 hover:bg-evergreen-900"
                  data-testid="terms-accept-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  I Accept
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
